import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

const qcFAQs = [
  {
    question: 'What exactly is QC in spreadsheet shopping?',
    answer:
      'QC stands for Quality Control. In the context of spreadsheet shopping, QC refers to the process of inspecting product photographs — typically taken by warehouse staff at your shipping agent — to verify that the item you ordered matches the listing description, is free from defects, and meets your quality expectations before it gets shipped internationally.',
  },
  {
    question: 'How do I request additional QC photos?',
    answer:
      'Most shopping agents allow you to request extra QC photos during the order processing stage. Simply message your agent and specify what you want to see — close-ups of logos, stitching details, tags, measurements with a ruler, or specific angles. Some agents charge a small fee for detailed photo sets, but it is well worth the investment to avoid receiving a subpar product.',
  },
  {
    question: 'What should I look for in QC photos?',
    answer:
      'Focus on these key areas: stitching quality and consistency, logo placement and accuracy, color accuracy compared to the listing, material texture and quality, sizing and measurements against the size chart, and any visible defects like stains, loose threads, or misaligned patterns. Compare the QC photos side by side with the original listing images to spot discrepancies.',
  },
  {
    question: 'What if QC photos reveal problems?',
    answer:
      'If QC photos show issues, you have options. You can request an exchange for a better batch if available, ask for a refund (policies vary by seller and agent), or accept the item if the flaws are minor. The key advantage of using QC is that you catch problems before the item ships internationally, saving you from expensive return shipping and long wait times.',
  },
];

export default function QCGuidePage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  return (
    <>
      <Navbar />
      <main>
        <article>
          <section className="relative w-full min-h-[400px] md:min-h-[500px] flex items-center overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Close%20up%20macro%20photography%20of%20a%20magnifying%20glass%20over%20fashion%20product%20tag%20with%20fabric%20texture%20visible%20soft%20diffused%20studio%20lighting%20warm%20ivory%20background%20product%20inspection%20quality%20check%20concept%20clean%20minimal%20editorial%20style%20no%20text&width=1600&height=700&seq=qc-hero-v2-x2&orientation=landscape')`,
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-50/50 via-background-50/30 to-background-50/80"></div>
            <div className="relative z-10 w-full px-4 md:px-6 pt-24 pb-12">
              <div className="max-w-3xl mx-auto text-center">
                <nav className="mb-5 text-xs md:text-sm text-foreground-500">
                  <a href="/" className="hover:text-primary-600 transition-colors">Home</a>
                  <span className="mx-2">›</span>
                  <span className="text-foreground-700">Guides</span>
                  <span className="mx-2">›</span>
                  <span className="text-primary-600 font-medium">QC Guide</span>
                </nav>
                <span className="inline-block px-3 py-1 rounded-full border border-accent-300/50 bg-accent-100/60 text-accent-700 text-xs font-medium mb-4">
                  Essential Reading
                </span>
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-foreground-950 leading-tight mb-4">
                  How To QC Products Like a Pro
                </h1>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto leading-relaxed">
                  Learn to inspect quality control photos, spot common issues, and ensure every product meets your standards before shipping.
                </p>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom text-sm md:text-base leading-relaxed text-foreground-700 space-y-7">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Why Quality Control Matters When Using Spreadsheets
                  </h2>
                  <p>
                    Shopping through a <strong>Superbuy Spreadsheet</strong> is incredibly efficient
                    — but efficiency means nothing if the products that arrive at your door do not match
                    your expectations. That is where Quality Control, or QC, becomes your most powerful
                    tool in the entire shopping workflow. QC is the safety net that catches problems
                    before they become expensive mistakes.
                  </p>
                  <p className="mt-3">
                    When you browse the spreadsheet and find a product you love, the listing photos
                    typically show the item in its best possible light. Professional lighting, careful
                    styling, and sometimes even photos of a completely different batch can create a gap
                    between what you see and what you get. QC photos — taken by warehouse staff at your
                    shipping agent under neutral conditions — reveal the truth about the actual product
                    sitting in the warehouse waiting for your approval.
                  </p>
                  <p className="mt-3">
                    Many spreadsheet users skip QC to save time or a small fee, only to receive items
                    with crooked logos, wrong colors, mismatched sizing, or visible defects. The cost
                    of returning an item internationally often exceeds the product price itself. Investing
                    a few extra minutes in QC review can save you weeks of frustration and significant
                    amounts of money.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    The QC Process: Step by Step
                  </h2>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="w-7 h-7 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold mt-0.5">1</div>
                      <div>
                        <strong className="text-foreground-900">Place Your Order Through an Agent</strong>
                        <p className="text-foreground-600 text-sm mt-1">
                          After finding a product in the spreadsheet, copy the link and paste it into
                          your shopping agent platform. Complete the purchase — at this stage, the agent
                          buys the item from the seller and waits for it to arrive at their warehouse.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-7 h-7 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold mt-0.5">2</div>
                      <div>
                        <strong className="text-foreground-900">Wait for Warehouse Arrival</strong>
                        <p className="text-foreground-600 text-sm mt-1">
                          It typically takes 2-5 days for the seller to ship the item to your agent's
                          warehouse. Once it arrives, the agent updates your order status and prepares
                          to take QC photographs.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-7 h-7 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold mt-0.5">3</div>
                      <div>
                        <strong className="text-foreground-900">Review the QC Photos</strong>
                        <p className="text-foreground-600 text-sm mt-1">
                          Your agent uploads standard QC photos — usually front, back, logo close-up,
                          tags, and overall item shots. Examine each photo carefully. Compare against
                          the original listing images. Look for stitching quality, logo accuracy, color
                          matching, and any visible flaws.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-7 h-7 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold mt-0.5">4</div>
                      <div>
                        <strong className="text-foreground-900">Request Additional Photos If Needed</strong>
                        <p className="text-foreground-600 text-sm mt-1">
                          If the standard photos do not show enough detail, request specific shots:
                          inside tags, measurements with a ruler, stitching close-ups, or specific
                          angles. Most agents accommodate these requests for a small fee.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-7 h-7 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold mt-0.5">5</div>
                      <div>
                        <strong className="text-foreground-900">Make Your Decision</strong>
                        <p className="text-foreground-600 text-sm mt-1">
                          Based on the QC review, decide whether to approve shipping, request an exchange,
                          or ask for a refund. Once you approve, the agent ships the item internationally.
                          This is your last chance to catch issues before the product leaves the country.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    What to Look For in QC Photos
                  </h2>
                  <p>
                    Knowing what to examine in QC photos separates experienced spreadsheet shoppers
                    from beginners. Here is a comprehensive checklist of what you should inspect in
                    every set of QC photographs before approving shipment.
                  </p>

                  <div className="overflow-x-auto mt-5">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b-2 border-background-300">
                          <th className="text-left py-3 pr-4 text-foreground-800 font-semibold">Check Area</th>
                          <th className="text-left py-3 pr-4 text-foreground-800 font-semibold">What to Look For</th>
                          <th className="text-left py-3 text-foreground-800 font-semibold">Red Flags</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-background-200">
                          <td className="py-3 pr-4 text-foreground-600 font-medium">Stitching</td>
                          <td className="py-3 pr-4 text-foreground-500">Clean, even stitches with consistent spacing</td>
                          <td className="py-3 text-red-500">Loose threads, skipped stitches, uneven lines</td>
                        </tr>
                        <tr className="border-b border-background-200">
                          <td className="py-3 pr-4 text-foreground-600 font-medium">Logo Placement</td>
                          <td className="py-3 pr-4 text-foreground-500">Centered and correctly positioned</td>
                          <td className="py-3 text-red-500">Crooked, off-center, wrong size or font</td>
                        </tr>
                        <tr className="border-b border-background-200">
                          <td className="py-3 pr-4 text-foreground-600 font-medium">Color Accuracy</td>
                          <td className="py-3 pr-4 text-foreground-500">Matches the listing images closely</td>
                          <td className="py-3 text-red-500">Significantly different shade or tone</td>
                        </tr>
                        <tr className="border-b border-background-200">
                          <td className="py-3 pr-4 text-foreground-600 font-medium">Material Quality</td>
                          <td className="py-3 pr-4 text-foreground-500">Consistent texture, no thin or shiny patches</td>
                          <td className="py-3 text-red-500">Uneven fabric, holes, stains, discoloration</td>
                        </tr>
                        <tr>
                          <td className="py-3 pr-4 text-foreground-600 font-medium">Sizing Tags</td>
                          <td className="py-3 pr-4 text-foreground-500">Tags match the size you ordered</td>
                          <td className="py-3 text-red-500">Wrong size tag or no tag at all</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-background-950 rounded-2xl p-6 md:p-8 text-center my-8">
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-background-50 mb-3">
                    Start Shopping With Confidence
                  </h3>
                  <p className="text-xs md:text-sm text-background-300 mb-5 max-w-md mx-auto">
                    Access QC-verified products from the updated Superbuy Spreadsheet.
                  </p>
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    Buy It Now
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-up-line text-sm"></i>
                    </div>
                  </a>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Common QC Pitfalls and How to Avoid Them
                  </h2>
                  <p>
                    Even experienced shoppers make QC mistakes. One of the most common errors is
                    rushing through the photo review because you are excited to receive your items.
                    Take your time — an extra five minutes of careful inspection can prevent weeks
                    of disappointment.
                  </p>
                  <p className="mt-3">
                    Another frequent pitfall is relying solely on the standard QC photos provided
                    by default. Standard sets typically include only front and back views, which may
                    not reveal issues with side profiles, interior construction, or small details.
                    Do not hesitate to request additional photos for items where quality is critical.
                  </p>
                  <p className="mt-3">
                    Lighting in QC photos can also be misleading. Warehouse lighting tends to be
                    harsh and fluorescent, which can wash out colors or create shadows that make
                    products look different from how they appear in natural light. When in doubt
                    about color accuracy, ask your agent to take a photo near a window or with a
                    color reference card.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-3">
                    {qcFAQs.map((faq, index) => (
                      <div
                        key={index}
                        className="rounded-2xl bg-background-100 border border-background-200 overflow-hidden"
                      >
                        <button
                          onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                          className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                        >
                          <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                            {faq.question}
                          </span>
                          <div className={`w-5 h-5 shrink-0 flex items-center justify-center transition-transform duration-300 ${openFAQ === index ? 'rotate-180' : ''}`}>
                            <i className="ri-arrow-down-s-line text-foreground-500"></i>
                          </div>
                        </button>
                        <div className={`overflow-hidden transition-all duration-300 ${openFAQ === index ? 'max-h-96 pb-4' : 'max-h-0'}`}>
                          <p className="px-5 text-xs md:text-sm text-foreground-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4">
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Final Thoughts on Quality Control
                  </h2>
                  <p>
                    Mastering the QC process transforms your <strong>Superbuy Spreadsheet</strong> shopping
                    experience from a gamble into a calculated, reliable system. The few extra minutes you
                    invest in reviewing photos and the small fees for additional inspection shots are
                    insignificant compared to the cost and frustration of receiving a defective product.
                  </p>
                  <p className="mt-3">
                    Remember: QC is your last line of defense before an item leaves the country. Once
                    it ships internationally, returns become complicated and expensive. Make every QC
                    review count, and over time you will develop an eye for spotting issues that less
                    experienced shoppers would miss entirely.
                  </p>
                </div>

                <div className="pt-6 pb-4 border-t border-background-200 flex flex-wrap gap-4 items-center justify-between">
                  <a
                    href="/"
                    className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors font-medium"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line text-sm"></i>
                    </div>
                    Back to Superbuy Spreadsheet Home
                  </a>
                  <a
                    href="/safety-guide"
                    className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors font-medium"
                  >
                    Next: Safety Guide
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </section>
        </article>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 text-center mb-8">
              Continue Reading
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  title: 'Complete Spreadsheet Guide',
                  desc: 'Master the entire shopping workflow',
                  link: '/superbuy-spreadsheet-guide',
                  img: 'https://readdy.ai/api/search-image?query=Minimal%20desk%20setup%20with%20laptop%20showing%20organized%20data%20grid%20surrounded%20by%20neatly%20arranged%20fashion%20pieces%20soft%20natural%20morning%20light%20cream%20linen%20background%20warm%20neutral%20tones%20editorial%20product%20photography%20clean%20composition&width=400&height=280&seq=rel-guide-v2-x2&orientation=landscape',
                },
                {
                  title: 'Shopping Safety Guide',
                  desc: 'Stay safe with trusted practices',
                  link: '/safety-guide',
                  img: 'https://readdy.ai/api/search-image?query=Padlock%20icon%20placed%20among%20folded%20clothing%20items%20on%20warm%20beige%20surface%20soft%20studio%20lighting%20security%20and%20trust%20concept%20minimal%20editorial%20photography%20clean%20aesthetic%20no%20text&width=400&height=280&seq=rel-safety-v2-x2&orientation=landscape',
                },
                {
                  title: 'Best Finds of 2026',
                  desc: 'Top community-vetted products',
                  link: '/superbuy-spreadsheet-guide',
                  img: 'https://readdy.ai/api/search-image?query=Curated%20collection%20of%20trendy%20fashion%20items%20laid%20out%20on%20white%20marble%20surface%20golden%20hour%20sunlight%20streaming%20through%20sheer%20curtains%20lifestyle%20editorial%20product%20photography%20warm%20aesthetic%20clean%20composition&width=400&height=280&seq=rel-finds-v2-x2&orientation=landscape',
                },
              ].map((item) => (
                <a
                  key={item.title}
                  href={item.link}
                  className="group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                    style={{ backgroundImage: `url('${item.img}')` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/65 via-foreground-950/20 to-transparent"></div>
                  <div className="absolute inset-0 flex flex-col justify-end p-5">
                    <h4 className="text-background-50 font-heading text-lg font-semibold leading-snug mb-1">
                      {item.title}
                    </h4>
                    <p className="text-background-50/75 text-xs">{item.desc}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-950 mb-4">
              Shop With Total Confidence
            </h2>
            <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-md mx-auto">
              Use QC knowledge to shop smarter and never receive a disappointing product again.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:border-foreground-500 transition-colors whitespace-nowrap w-full sm:w-auto justify-center"
              >
                Superbuy Sheet
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </div>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap w-full sm:w-auto justify-center"
              >
                Buy It Now
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </div>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'How To QC Products Like a Pro — Superbuy Spreadsheet Quality Control Guide',
            description:
              'Learn to inspect quality control photos, spot common issues, and ensure every product meets your standards before shipping with the Superbuy Spreadsheet.',
            datePublished: '2026-03-20',
            dateModified: '2026-07-13',
            author: {
              '@type': 'Organization',
              name: 'Superbuy Spread',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: import.meta.env.VITE_SITE_URL || 'https://superbuyspread.autos',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Guides',
                item: (import.meta.env.VITE_SITE_URL || 'https://superbuyspread.autos') + '/',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: 'QC Guide',
                item: (import.meta.env.VITE_SITE_URL || 'https://superbuyspread.autos') + '/qc-guide',
              },
            ],
          }),
        }}
      />
    </>
  );
}